import { AuthView } from "@daveyplate/better-auth-ui"
import { t } from "@lingui/core/macro"
import { createFileRoute, useParams } from "@tanstack/react-router"
import { Sun, Moon, Monitor } from "lucide-react"
import { useTheme } from "next-themes"
import { useState, useEffect } from "react"
import { BrandLogo } from "@/components/BrandLogo"
import { useBrandConfig } from "@/lib/brand-context"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/auth/$pathname")({
  validateSearch: (search) => {
    return {
      from: (search.from as string) || "/",
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const pathname = useParams({ from: Route.id })["pathname"] || "sign-in"
  const { from } = Route.useSearch()
  const brandConfig = useBrandConfig()

  return (
    <div className="dark:bg-brand-darkblue-900 relative min-h-screen bg-white">
      {/* Blueprint Grid Background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.015] dark:opacity-[0.15]"
        style={{
          backgroundImage: `
            linear-gradient(to right, var(--color-brand-darkblue-300) 1px, transparent 1px),
            linear-gradient(to bottom, var(--color-brand-darkblue-300) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Language switcher - integrated into header */}
      <header className="border-brand-darkblue-50 dark:border-brand-darkblue-500 dark:bg-brand-darkblue-900/90 relative z-50 flex items-center justify-between border-b bg-white/90 px-6 py-4 backdrop-blur-sm">
        <BrandLogo variant="primary" size="md" />
        <div className="flex items-center space-x-2">
          <ModeToggle />
        </div>
      </header>

      <main className="relative flex min-h-[calc(100vh-73px)] items-center justify-center p-8">
        {/* Two Column Layout */}
        <div className="flex w-full max-w-6xl gap-12">
          {/* Left: Branding Section */}
          <div className="hidden w-1/2 flex-col justify-center space-y-10 lg:flex">
            {/* Logo & Title Block */}
            <div className="space-y-6">
              <div>
                <h1 className="text-brand-darkblue-900 font-serif text-5xl font-bold leading-tight dark:text-white">
                  {pathname === "sign-in" ? t`Welcome Back` : t`Start Planning`}
                </h1>
                <p className="text-brand-darkblue-300 dark:text-brand-darkblue-150 mt-4 text-base">
                  {t`Retail Space Optimization Platform`}
                </p>
              </div>
            </div>

            {/* Feature List */}
            <div className="border-brand-orange-500 space-y-5 border-l-2 pl-6">
              <div className="space-y-2">
                <h3 className="text-brand-darkblue-900 font-medium dark:text-white">
                  {t`Shelf Detection`}
                </h3>
                <p className="text-brand-darkblue-300 dark:text-brand-darkblue-150 text-sm">
                  {t`Computer vision algorithms identify shelf structures from floor plans with precision`}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-brand-darkblue-900 font-medium dark:text-white">
                  {t`Category Assignment`}
                </h3>
                <p className="text-brand-darkblue-300 dark:text-brand-darkblue-150 text-sm">
                  {t`Constraint-based optimization assigns product categories to maximize flow and revenue`}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-brand-darkblue-900 font-medium dark:text-white">
                  {t`3D Visualization`}
                </h3>
                <p className="text-brand-darkblue-300 dark:text-brand-darkblue-150 text-sm">
                  {t`Interactive rendering provides spatial understanding of optimized layouts`}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Auth Form Section */}
          <div className="flex w-full items-center justify-center lg:w-1/2">
            <div className="w-full max-w-md">
              {/* Mobile Logo */}
              <div className="mb-8 lg:hidden">
                <BrandLogo variant="primary" size="md" />
              </div>

              <div className="border-brand-darkblue-150 dark:border-brand-darkblue-500 dark:bg-brand-darkblue-900/50 border bg-white/80 p-8 shadow-sm backdrop-blur-sm">
                {/* Form Header */}
                <div className="border-brand-darkblue-150 dark:border-brand-darkblue-500 mb-8 border-b pb-6">
                  <h2 className="text-brand-darkblue-900 font-serif text-3xl font-bold dark:text-white">
                    {pathname === "sign-in" ? t`Account Access` : t`New Account`}
                  </h2>
                  <p className="text-brand-darkblue-300 dark:text-brand-darkblue-150 mt-2 text-sm">
                    {pathname === "sign-in"
                      ? t`Enter your credentials to continue`
                      : t`Complete the form to get started`}
                  </p>
                </div>

                {/* Auth View */}
                <div className="auth-form-container">
                  <AuthView pathname={pathname} callbackURL={from} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Technical Note */}
      <footer className="border-brand-darkblue-50 dark:border-brand-darkblue-500 dark:bg-brand-darkblue-900/90 absolute bottom-0 left-0 right-0 border-t bg-white/90 px-6 py-3 backdrop-blur-sm">
        <div className="text-brand-darkblue-300 dark:text-brand-darkblue-150 font-mono text-xs">
          {`${brandConfig.name.toUpperCase()} — Retail Optimization System`}
        </div>
      </footer>
    </div>
  )
}

// Theme toggle component using next-themes
function ModeToggle() {
  const { theme, setTheme } = useTheme()
  const [showDropdown, setShowDropdown] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return (
      <div className="relative">
        <button
          className="relative rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          disabled
        >
          <div className="relative h-[1.2rem] w-[1.2rem]">
            <Monitor className="h-[1.2rem] w-[1.2rem] text-gray-600 dark:text-gray-400" />
          </div>
          <span className="sr-only">{t`Toggle theme`}</span>
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <div className="relative h-[1.2rem] w-[1.2rem]">
          {theme === "system" ? (
            <Monitor className="h-[1.2rem] w-[1.2rem] text-gray-600 dark:text-gray-400" />
          ) : (
            <>
              <Sun className="absolute inset-0 h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute inset-0 h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </>
          )}
        </div>
        <span className="sr-only">{t`Toggle theme`}</span>
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-md border bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="py-1">
            <button
              onClick={() => {
                setTheme("light")
                setShowDropdown(false)
              }}
              className={cn(
                "flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                theme === "light"
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                  : "text-gray-700 dark:text-gray-300",
              )}
            >
              <Sun className="mr-2 h-4 w-4" />
              {t`Light`}
            </button>
            <button
              onClick={() => {
                setTheme("dark")
                setShowDropdown(false)
              }}
              className={cn(
                "flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                theme === "dark"
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                  : "text-gray-700 dark:text-gray-300",
              )}
            >
              <Moon className="mr-2 h-4 w-4" />
              {t`Dark`}
            </button>
            <button
              onClick={() => {
                setTheme("system")
                setShowDropdown(false)
              }}
              className={cn(
                "flex w-full items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                theme === "system"
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                  : "text-gray-700 dark:text-gray-300",
              )}
            >
              <Monitor className="mr-2 h-4 w-4" />
              {t`System`}
            </button>
          </div>
        </div>
      )}

      {showDropdown && (
        <div
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Click-away overlay pattern requires div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowDropdown(false)
            }
          }}
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Click-away overlay requires div
          role="button"
          tabIndex={-1}
          aria-label="Close theme menu"
        />
      )}
    </div>
  )
}
